import React, { useState } from 'react';
import { CustomizationCarousel } from './common/CustomizationCarousel';
import { OpacitySlider } from './OpacitySlider';
import { ColorPickerWithPresets } from './common/ColorPickerWithPresets';
import { WatermarkPreview } from './WatermarkPreview';
import { AdvancedSettings } from './common/AdvancedSettings';
import styled from 'styled-components';

interface WatermarkCustomizationProps {
  watermark: string;
  setWatermark: (watermark: string) => void;
  watermarkColor: string;
  setWatermarkColor: (color: string) => void;
  watermarkOpacity: number;
  setWatermarkOpacity: (opacity: number) => void;
}

export const WatermarkCustomization: React.FC<WatermarkCustomizationProps> = ({
  watermark,
  setWatermark,
  watermarkColor,
  setWatermarkColor,
  watermarkOpacity,
  setWatermarkOpacity,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const watermarkOptions = [
    { value: 'none', label: 'None' },
    { value: 'skull', label: 'Skull' },
    { value: 'candycane', label: 'Candy Cane' },
    { value: 'snowflake', label: 'Snowflake' },
    { value: 'santaclaus', label: 'Santa Claus' },
    { value: 'reindeer', label: 'Reindeer' },
    { value: 'christmastree', label: 'Christmas Tree' },
  ];

  const itemsPerPage = 6;
  const totalPages = Math.ceil(watermarkOptions.length / itemsPerPage);

  const handleWatermarkSelect = (value: string) => {
    setWatermark(value);
    if (value !== 'none' && watermarkOpacity === 0) {
      setWatermarkOpacity(0.3);
    }
  };

  return (
    <Section>
      <CustomizationCarousel
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
      >
        {watermarkOptions
          .slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage)
          .map((option) => (
            <OptionBox
              key={option.value}
              active={watermark === option.value}
              onClick={() => handleWatermarkSelect(option.value)}
            >
              <PreviewContainer>
                <WatermarkPreview 
                  type={option.value}
                  color={watermarkColor}
                  opacity={1}
                />
              </PreviewContainer>
              <OptionLabel>{option.label}</OptionLabel>
            </OptionBox>
        ))}
      </CustomizationCarousel>

      {watermark !== 'none' && (
        <AdvancedSettings
  showAdvanced={showAdvanced}
  setShowAdvanced={setShowAdvanced}
  titlePrefix="Advanced Watermark"
>
          <ColorPickerWithPresets
            label="Watermark Color"
            color={watermarkColor}
            onChange={setWatermarkColor}
          />
          <OpacitySlider
            label="Watermark Opacity"
            value={watermarkOpacity}
            onChange={setWatermarkOpacity}
          />
        </AdvancedSettings>
      )}
    </Section>
  );
};

const Section = styled.section`
  margin-top: 24px;
`;

const OptionBox = styled.button<{ active: boolean }>`
  min-width: 120px;
  height: 120px;
  background: white;
  border: 2px solid ${props => props.active ? '#ff6320' : '#e9ecef'};
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  &:hover {
    border-color: #ff6320;
  }
`;

const PreviewContainer = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionLabel = styled.span`
  font-size: 12px;
  color: #666;
  text-align: center;
`;